import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Video } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MessageThread from "@/components/messaging/MessageThread";
import { VirtualMeeting } from "@/components/messaging/VirtualMeeting";
import { supabase } from "@/lib/supabase";

interface MessageThreadOnlyProps {
	conversationId: string;
	conversationData?: {
		doula_user?: {
			id: string;
			full_name: string;
			avatar_url: string;
		};
	} | unknown | undefined;
	userType?: "parent" | "doula";
}

const MessageThreadOnly: React.FC<MessageThreadOnlyProps> = ({
	conversationId,
	conversationData,
	userType = "parent",
}) => {
	const [activeTab, setActiveTab] = useState<"messages" | "meetings">(
		"messages",
	);
	const [selectedConversationData, setSelectedConversationData] = useState<{
		participantName: string;
		participantAvatar: string;
		doulaUserId: string;
	} | null>(null);

	React.useEffect(() => {
		// Use conversation data if provided, otherwise load from database
		if (conversationData) {
			setSelectedConversationData({
				participantName: conversationData.doula_user?.full_name || "Doula",
				participantAvatar:
					conversationData.doula_user?.avatar_url || "/placeholder.svg",
				doulaUserId: conversationData.doula_user?.id || "",
			});
		} else {
			// Load conversation data to get participant info
			const loadConversationData = async () => {
				try {
					const { data: conversation, error } = await supabase
						.from("conversations")
						.select(
							`
              doula_user:users!conversations_doula_user_id_fkey(
                id,
                full_name,
                avatar_url
              )
            `,
						)
						.eq("id", conversationId)
						.single();

					if (!error && conversation) {
						setSelectedConversationData({
							participantName: conversation.doula_user?.full_name || "Doula",
							participantAvatar:
								conversation.doula_user?.avatar_url || "/placeholder.svg",
							doulaUserId: conversation.doula_user?.id || "",
						});
					}
				} catch (error) {
					console.error("Error loading conversation data:", error);
					setSelectedConversationData({
						participantName: "Doula",
						participantAvatar: "/placeholder.svg",
						doulaUserId: "",
					});
				}
			};

			if (conversationId) {
				loadConversationData();
			}
		}
	}, [conversationId, conversationData]);

	const handleStartVideoCall = () => {
		setActiveTab("meetings");
	};

	if (!selectedConversationData) {
		return (
			<Card className="glass-card border-0 shadow-lg h-[600px] flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading conversation...</p>
				</div>
			</Card>
		);
	}

	return (
		<Card className="glass-card border-0 shadow-lg h-[600px] flex flex-col rounded-lg">
			{/* Tab Navigation */}
			<div className="border-b bg-gray-50">
				<div className="flex">
					<Button
						variant={activeTab === "messages" ? "default" : "ghost"}
						onClick={() => setActiveTab("messages")}
						className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
					>
						<MessageCircle className="h-4 w-4 mr-2" />
						Messages
					</Button>
					<Button
						variant={activeTab === "meetings" ? "default" : "ghost"}
						onClick={() => setActiveTab("meetings")}
						className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
					>
						<Video className="h-4 w-4 mr-2" />
						Meetings
					</Button>
				</div>
			</div>

			{/* Tab Content */}
			<div className="flex-1 overflow-hidden">
				{activeTab === "messages" ? (
					<MessageThread
						conversationId={conversationId}
						participantName={selectedConversationData.participantName}
						participantAvatar={selectedConversationData.participantAvatar}
						userType={userType}
					/>
				) : (
					<VirtualMeeting
						conversationId={conversationId}
						participantName={selectedConversationData.participantName}
						onBackToMessages={() => setActiveTab("messages")}
						doulaUserId={selectedConversationData.doulaUserId}
					/>
				)}
			</div>
		</Card>
	);
};

export default MessageThreadOnly;
